import React, { useState } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  SplitCellsOutlined,
  ColumnWidthOutlined,
  DragOutlined,
} from "@ant-design/icons";
import { Button, Modal, Tooltip } from "antd";
import Section from "./Section_improved";
import TextInput from "./TextInput";
import TextAreaInput from "./TextAreaInput";
import NumberInput from "./NumberInput";
import DateInput from "./DateInput";
import Info from "./Info";
import SwitchInput from "./SwitchInput";
import TimeInput from "./TimeInput";
import CheckboxInput from "./CheckboxInput";
import DropdownInput from "./DropdownInput";
import MultipleChoiceInput from "./MultipleChoiceInput";
import RadioInput from "./RadioInput";
import AddressInput from "./AddressInput";
import LinkInput from "./LinkInput";
import DividerInput from "./DividerInput";
import ImageInput from "./ImageInput";
import { Attachments } from "./Attachments";
import ContactInput from "./ContactInput";
import TableInput from "./TableInput";
import { useTranslation } from "react-i18next";
import UserInput from "./UserInput";
import RatingInput from "./RatingInput";
import RatingScaleInput from "./RatingScaleInput";
import { resolveFieldTranslation } from "../../../utils/formTranslations";

const HoverWrapper = React.memo((props) => {
  const {
    formType,
    data,
    activeElement,
    isActive,
    // Not used here, but a section renders from inside HoverWrapper and needs it, so it
    // is destructured and forwarded explicitly rather than left in `rest` - leaving it
    // in `rest` made every field re-render whenever a drag started.
    activeDragItem,
    handleWidgetWidth,
    addWidgetToContainer,
    isDragging,
    isSortable,
    dragHandleProps,
    ...rest
  } = props;
  const { t, i18n } = useTranslation();
  const [isHovering, setIsHovering] = useState(false);
  let renderElement = null;
  // Resolve user-facing text for the active app locale; `data` stays canonical
  // for editor chrome (selection, copy, delete) so editing always targets the
  // default-language configuration.
  const localized = resolveFieldTranslation(data, i18n.language);

  switch (data.formType) {
    case "section":
      // Don't pass parent's sectionId to nested section - it should use its own id
      const { sectionId: parentSectionId, ...restWithoutSectionId } = rest;
      renderElement = (
        <Section
          activeElement={activeElement}
          activeDragItem={activeDragItem}
          handleWidgetWidth={handleWidgetWidth}
          addWidgetToContainer={addWidgetToContainer}
          onSectionReorder={rest.handleSectionReorder}
          {...localized}
          {...restWithoutSectionId}
        />
      );
      break;

    case "divider":
      renderElement = <DividerInput {...localized} {...rest} />;
      break;

    case "addressField":
      renderElement = <AddressInput {...localized} {...rest} />;
      break;

    case "attachment":
      renderElement = <Attachments {...localized} {...rest} />;
      break;

    case "contactField":
      renderElement = <ContactInput {...localized} {...rest} />;
      break;

    case "inputForm":
      renderElement = <TextInput {...localized} {...rest} />;
      break;

    case "textAreaForm":
      renderElement = <TextAreaInput {...localized} {...rest} />;
      break;

    case "numberForm":
      renderElement = <NumberInput {...localized} {...rest} />;
      break;

    case "dateForm":
      renderElement = <DateInput {...localized} {...rest} />;
      break;

    case "infoForm":
      renderElement = <Info {...localized} {...rest} />;
      break;

    case "imageForm":
      renderElement = <ImageInput {...localized} {...rest} />;
      break;

    case "switchForm":
      renderElement = <SwitchInput {...localized} {...rest} />;
      break;

    case "timeForm":
      renderElement = <TimeInput {...localized} {...rest} />;
      break;

    case "checkboxField":
      renderElement = <CheckboxInput {...localized} {...rest} />;
      break;

    case "dropdownField":
      renderElement = <DropdownInput {...localized} {...rest} />;
      break;

    case "linkField":
      renderElement = <LinkInput {...localized} {...rest} />;
      break;

    case "multipleChoiceField":
      renderElement = <MultipleChoiceInput {...localized} {...rest} />;
      break;

    case "radioField":
      renderElement = <RadioInput {...localized} {...rest} />;
      break;
    case "userForm":
      renderElement = <UserInput {...localized} {...rest} />;
      break;

    case "tableForm":
      renderElement = <TableInput {...rest} {...localized} lng={i18n.language} />;
      break;

    case "ratingField":
      renderElement = <RatingInput {...localized} {...rest} />;
      break;

    case "ratingScaleField":
      renderElement = <RatingScaleInput {...localized} {...rest} />;
      break;

    default:
      renderElement = null;
  }

  const handleMouseOver = (event) => {
    if (rest.previewMode) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setIsHovering(true);
  };

  const handleMouseOut = (event) => {
    if (rest.previewMode) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setIsHovering(false);
  };
  const handleElementClick = (event) => {
    if (rest.previewMode) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (props.widgetClickHandler) {
      props.widgetClickHandler({ ...data });
    }
  };

  const changeWidgetWidth = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    handleWidgetWidth(id);
  };

  const handleWidgetCopy = (event, data) => {
    event.preventDefault();
    event.stopPropagation();
    props.handleWidgetCopy(data);
  };
  return (
    <div
      style={{
        minHeight: data.formType !== "divider" ? "87px" : "auto",
        cursor: isSortable ? (isDragging ? "grabbing" : "grab") : "pointer",
        // Remove border style to prevent layout shifts - handled by CSS
        borderRadius: isDragging ? "4px" : undefined,
        backgroundColor: isDragging ? "#f0f8ff" : "transparent",
        position: "relative",
      }}
      className={`border-1 border-black ${
        ((isActive ?? activeElement?.id === data?.id) || isHovering) && !rest.previewMode
          ? "selected-element"
          : ""
      } hover-wrapper ${data.formType === "section" ? "section-drop" : ""} ${
        isDragging ? "dragging" : ""
      }`}
      onClick={handleElementClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      {" "}
      {/* Show drag indicator for sortable elements */}
      {/* {isSortable && !rest.previewMode && (
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: "4px",
            padding: "2px 6px",
            fontSize: "10px",
            color: "#666",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <DragOutlined /> Drag to reorder
        </div>
      )} */}
      {isHovering && !isSortable ? (
        <div className="widget-actions">
          {" "}
          {/* <Tooltip title="Drag to reorder">
            <Button
              size="small"
              icon={<DragOutlined />}
              {...(dragHandleProps || props.dragHandleProps)}
              style={{
                cursor: "grab",
                border: "1px dashed #ccc",
              }}
              onMouseDown={(e) => {
                e.stopPropagation(); // Prevent element selection when dragging
              }}
            />
          </Tooltip> */}
          <Tooltip title={t('Copy')}>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={(event) => handleWidgetCopy(event, data)}
            ></Button>
          </Tooltip>
          {data.formType !== "section" && (
            <Tooltip
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ColumnWidthOutlined style={{ marginRight: 7 }} />{" "}
                  {data.span === "24" ? 2 : 1}
                </div>
              }
            >
              <Button
                size="small"
                icon={<SplitCellsOutlined />}
                onClick={(e) => changeWidgetWidth(e, data.id)}
              />
            </Tooltip>
          )}
          <Tooltip title={t('Delete')}>
            <Button
              size="small"
              icon={<DeleteOutlined />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                Modal.confirm({
                  title: t("Confirm"),
                  content: t("Are you sure to remove this widget?"),
                  okText: t("Yes"),
                  onOk: () => {
                    props.handleWidgetDelete(data.id);
                    Modal.destroyAll();
                  },
                  footer: (_, { OkBtn, CancelBtn }) => (
                    <>
                      <CancelBtn />
                      <OkBtn />
                    </>
                  ),
                });
              }}
            />
          </Tooltip>
        </div>
      ) : isHovering && isSortable ? (
        <div className="widget-actions">
          {/* Add drag handle for sortable elements */}
          {/* <Tooltip title="Drag to reorder">
            <Button
              size="small"
              icon={<DragOutlined />}
              {...(dragHandleProps || {})}
              style={{
                cursor: "grab",
                border: "1px dashed #ccc",
              }}
              onMouseDown={(e) => {
                e.stopPropagation(); // Prevent element selection when dragging
              }}
            />
          </Tooltip> */}
          <Tooltip title={t('Copy')}>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={(event) => handleWidgetCopy(event, data)}
            ></Button>
          </Tooltip>
          {data.formType !== "section" && (
            <Tooltip
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ColumnWidthOutlined style={{ marginRight: 7 }} />{" "}
                  {data.span === "24" ? 2 : 1}
                </div>
              }
            >
              <Button
                size="small"
                icon={<SplitCellsOutlined />}
                onClick={(e) => changeWidgetWidth(e, data.id)}
              />
            </Tooltip>
          )}
          <Tooltip title={t('Delete')}>
            <Button
              size="small"
              icon={<DeleteOutlined />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                Modal.confirm({
                  title: t("Confirm"),
                  content: t("Are you sure to remove this widget?"),
                  okText: t("Yes"),
                  onOk: () => {
                    props.handleWidgetDelete(data.id);
                    Modal.destroyAll();
                  },
                  footer: (_, { OkBtn, CancelBtn }) => (
                    <>
                      <CancelBtn />
                      <OkBtn />
                    </>
                  ),
                });
              }}
            />
          </Tooltip>
        </div>
      ) : null}
      {renderElement}
    </div>
  );
});
HoverWrapper.displayName = "HoverWrapper";

export default HoverWrapper;
